import { createSelector } from '@reduxjs/toolkit';

import { pruneInactiveNodes } from '@/shared/filters/filter-tree';
import type { RootState } from '@/store/root-reducer';

import { countActiveRules } from '../../_shared/domain/filter-tree';
import { mergeSelection } from '../../_shared/domain/merge-selection';
import type { MethodScopeData } from '../../_shared/domain/method-scope';
import type { ListMethod } from '../domain/list-draft.model';
import { validateDraft } from '../domain/list-draft.validation';
import { selectedCount } from '../domain/row-selection';
import { buildStepKeys } from '../domain/steps';

const selectAddList = (state: RootState) => state.addList;

export const selectDraft = (state: RootState) => selectAddList(state).draft;
export const selectScope = (state: RootState) => selectAddList(state).draft.scope;
export const selectMethods = (state: RootState) => selectAddList(state).draft.methods;
export const selectStep = (state: RootState) => selectAddList(state).step;
export const selectSaveStatus = (state: RootState) => selectAddList(state).saveStatus;
export const selectSavePhase = (state: RootState) => selectAddList(state).savePhase;
export const selectError = (state: RootState) => selectAddList(state).error;
export const selectCreatedListId = (state: RootState) => selectAddList(state).createdListId;
export const selectValidationErrors = (state: RootState) => selectAddList(state).validationErrors;

export const selectIsSaving = (state: RootState) => selectAddList(state).saveStatus === 'saving';

/** Derived: is the current draft valid? Memoised on the draft reference. */
export const selectIsDraftValid = createSelector(
  selectDraft,
  (draft) => Object.keys(validateDraft(draft)).length === 0,
);

// ── Selection ──
export const selectExcludeDuplicates = (state: RootState) => selectAddList(state).excludeDuplicates;
export const selectImportedProfileIds = (state: RootState) => selectAddList(state).importedProfileIds;
export const selectImportedListName = (state: RootState) => selectAddList(state).importedListName;
export const selectFileImportData = (state: RootState) => selectAddList(state).fileImportData;
export const selectFileUpload = (state: RootState) => selectAddList(state).fileUpload;
export const selectFileSize = (state: RootState) => selectAddList(state).fileSize;
export const selectFileProgress = (state: RootState) => selectAddList(state).fileProgress;
export const selectEnrichNew = (state: RootState) => selectAddList(state).enrichNew;
export const selectEnrichStale = (state: RootState) => selectAddList(state).enrichStale;

// ── Filters ──
export const selectRootGroup = (state: RootState) => selectAddList(state).filters.rootGroup;

/** Pruned filter tree sent to the server. `null` = no active filters. The
 * 500ms debounce lives in the saga, NOT here. Memoised on the tree reference. */
export const selectServerFilters = createSelector(selectRootGroup, (rootGroup) => {
  const pruned = pruneInactiveNodes(rootGroup);
  return pruned.children.length > 0 ? pruned : null;
});

/** Number of active rules across the tree (feeds the filter-bar badge). */
export const selectActiveRuleCount = createSelector(selectRootGroup, (rootGroup) =>
  countActiveRules(rootGroup),
);

// ── Profile table ──
export const selectProfileRows = (state: RootState) => selectAddList(state).profiles.rows;
export const selectProfileTotal = (state: RootState) => selectAddList(state).profiles.total;
export const selectProfilePage = (state: RootState) => selectAddList(state).profiles.page;
export const selectProfilePageSize = (state: RootState) => selectAddList(state).profiles.pageSize;
export const selectProfilesLoading = (state: RootState) => selectAddList(state).profiles.loading;
export const selectRowSelection = (state: RootState) => selectAddList(state).rowSelection;
/** How many results are ticked (board E3). */
export const selectSelectedRowCount = createSelector(
  selectRowSelection,
  selectProfileTotal,
  (selection, total) => selectedCount(selection, total),
);

// ── Async results ──
export const selectDuplicateCount = (state: RootState) => selectAddList(state).duplicateCount;
export const selectDuplicateLoading = (state: RootState) => selectAddList(state).duplicateLoading;
export const selectEnrichment = (state: RootState) => selectAddList(state).enrichment;
export const selectEnrichmentLoading = (state: RootState) =>
  selectAddList(state).enrichmentStatus === 'loading';
/** The buckets could not be computed: the step offers a retry, and no list is created meanwhile. */
export const selectEnrichmentFailed = (state: RootState) =>
  selectAddList(state).enrichmentStatus === 'failed';
export const selectNlSearching = (state: RootState) => selectAddList(state).nlSearching;

// ── Licence dropdown ──
export const selectLicences = (state: RootState) => selectAddList(state).licences;
export const selectLicencesLoading = (state: RootState) => selectAddList(state).licencesLoading;
/** Names of the picked licence's lists (next number, preset badge); `null` until known. */
export const selectLicenceListNames = (state: RootState) => selectAddList(state).licenceListNames;

/**
 * Smart-merge of filter-derived + imported ids (the "Select profiles" summary).
 *
 * Each source is gated on its own method, so the recap counts exactly what
 * `buildSavePlan` will send. Without that gate the summary quietly reported
 * (and the save quietly shipped) profiles from a method the user had unselected
 * (docs/product-issues.md PI-40).
 */
export const selectImportStats = createSelector(
  selectMethods,
  selectEnrichment,
  selectImportedProfileIds,
  (methods, enrichment, importedIds) =>
    mergeSelection(
      methods.includes('select') ? enrichment.filterOnlyIds : [],
      methods.includes('import') ? importedIds : [],
    ),
);

/** The raw, un-merged sources the confirm step hands to `buildSavePlan`. */
export const selectFilterOnlyIds = (state: RootState) =>
  selectAddList(state).enrichment.filterOnlyIds;


// ── "Import from existing list" step ──
const selectListImport = (state: RootState) => selectAddList(state).listImport;
export const selectSourceOrigin = (state: RootState) => selectListImport(state).origin;
export const selectSourceLicenceId = (state: RootState) => selectListImport(state).sourceLicenceId;
export const selectSourceLists = (state: RootState) => selectListImport(state).sourceLists;
export const selectSourceListsLoading = (state: RootState) =>
  selectListImport(state).sourceListsLoading;
export const selectIsImporting = (state: RootState) => selectListImport(state).importing;
export const selectRawImportedIds = (state: RootState) => selectListImport(state).rawImportedIds;
export const selectImportExcludeDuplicates = (state: RootState) =>
  selectListImport(state).excludeDuplicates;

export const selectImportDuplicateIds = (state: RootState) =>
  selectListImport(state).duplicateIds;

/** Duplicates found on the last import; derived, never stored. */
export const selectImportDuplicateCount = createSelector(
  selectImportDuplicateIds,
  (ids) => ids.length,
);

// ── Imported-profiles preview table ──
export const selectImportPreviewRows = (state: RootState) =>
  selectListImport(state).preview.rows;
export const selectImportPreviewTotal = (state: RootState) =>
  selectListImport(state).preview.total;
export const selectImportPreviewLoading = (state: RootState) =>
  selectListImport(state).preview.loading;
export const selectImportPreviewPage = (state: RootState) =>
  selectListImport(state).preview.page;
export const selectImportPreviewPageSize = (state: RootState) =>
  selectListImport(state).preview.pageSize;

// ── Method ownership ──

/**
 * Which methods currently hold data. Unselecting one discards what it owned, so
 * the details step reads this to warn before the loss rather than after.
 *
 * Declared last: it composes selectors defined further up the file.
 */
export const selectMethodScopeData = createSelector(
  selectFileUpload,
  selectFileImportData,
  selectRawImportedIds,
  selectActiveRuleCount,
  (fileUpload, fileImportData, rawImportedIds, activeRuleCount): MethodScopeData => ({
    file: fileImportData !== null || fileUpload.fileName !== null,
    import: rawImportedIds.length > 0,
    select: activeRuleCount > 0,
  }),
);

// ── List summary (the right-hand card) ──

/**
 * The total the list will be created with: the merged filter + imported ids,
 * plus the rows the file import creates or updates. Only final once the confirm
 * step has loaded its buckets.
 */
export const selectDisplayTotal = createSelector(
  selectImportStats,
  selectFileImportData,
  (importStats, fileImportData) =>
    importStats.finalTotal +
    (fileImportData
      ? fileImportData.analyzeResult.to_create + fileImportData.analyzeResult.to_update
      : 0),
);

/**
 * The profiles the file import will create, counted in the "New" bucket and
 * enriched after the import when it is ticked (docs/product-issues.md PI-115).
 * None when the file holds full profile data: they are marked as enriched today.
 */
export const selectFileNewCount = createSelector(
  selectMethods,
  selectFileImportData,
  (methods, fileImportData) =>
    methods.includes('file') && fileImportData && !fileImportData.isEnriched
      ? fileImportData.analyzeResult.to_create
      : 0,
);

export type SummarySourceMethod = Exclude<ListMethod, 'empty'>;

export interface SummarySource {
  method: SummarySourceMethod;
  /** Position of the source's step in the running wizard (1-based, as shown). */
  stepNumber: number;
  /** Profiles the source brings, once known; `null` while its step is still to do. */
  count: number | null;
  /** The file or list the profiles come from, when there is one. */
  origin: string | null;
}

export interface ListSummary {
  empty: boolean;
  sources: SummarySource[];
  /** The list's total, known only on the confirm step once its buckets load. */
  total: number | null;
}

const SOURCE_METHODS: readonly SummarySourceMethod[] = ['file', 'import', 'select'];

/**
 * What the summary card knows about the list being built; no request of its
 * own: every count is one a step has already fetched. Duplicates between
 * sources are only resolved at Confirm, so the total waits for it.
 */
export const selectListSummary = createSelector(
  selectMethods,
  selectStep,
  selectFileImportData,
  selectFileUpload,
  selectImportedProfileIds,
  selectImportedListName,
  selectActiveRuleCount,
  selectSelectedRowCount,
  selectEnrichmentLoading,
  selectEnrichmentFailed,
  selectDisplayTotal,
  (
    methods,
    step,
    fileImportData,
    fileUpload,
    importedIds,
    importedListName,
    activeRuleCount,
    selectedRowCount,
    enrichmentLoading,
    enrichmentFailed,
    displayTotal,
  ): ListSummary => {
    const keys = buildStepKeys(methods);
    const reached = (method: SummarySourceMethod) => keys.indexOf(method) <= step;
    const countOf = (method: SummarySourceMethod): number | null => {
      if (method === 'file') {
        return fileImportData
          ? fileImportData.analyzeResult.to_create + fileImportData.analyzeResult.to_update
          : null;
      }
      if (method === 'import') return importedIds.length > 0 ? importedIds.length : null;
      // Without a filter the table lists nothing to tick.
      return reached('select') && activeRuleCount > 0 ? selectedRowCount : null;
    };
    const originOf = (method: SummarySourceMethod): string | null => {
      if (method === 'file') return fileImportData?.fileName ?? fileUpload.fileName;
      if (method === 'import') return importedListName || null;
      return null;
    };

    const sources = SOURCE_METHODS.filter((m) => keys.includes(m)).map((method) => ({
      method,
      stepNumber: keys.indexOf(method) + 1,
      count: countOf(method),
      origin: originOf(method),
    }));
    const onConfirm = keys[step] === 'confirm';

    return {
      empty: methods.includes('empty'),
      sources,
      total: onConfirm && !enrichmentLoading && !enrichmentFailed ? displayTotal : null,
    };
  },
);
